import React from 'react'
import { captureException } from '@sentry/browser'

function FallbackComponent() {
  return (
    <div>
      Oh No!
      <button onClick={() => window.location.reload()}>Reload the page</button>
    </div>
  )
}

interface ErrorBoundaryProps {
  /**
   * Called when the boundry catches an unhandled error
   */
  onError?: () => void
  /**
   * Rendered when a component throws an error.
   */
  FallbackComponent?: React.ElementType
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps> {
  static defaultProps = {
    FallbackComponent,
  }

  state = {
    error: null,
    info: null,
  }

  componentDidCatch(error, info) {
    const { onError } = this.props

    if (typeof onError === 'function') {
      try {
        onError.call(this, error, info ? info.componentStack : '')
      } catch (ignoredError) {}
    }

    console.error(error, info)
    captureException(error) // send error to sentry
    this.setState({ error, info })
  }

  render() {
    const { children, FallbackComponent } = this.props
    const { error, info } = this.state

    if (error !== null) {
      return (
        <FallbackComponent
          componentStack={info ? info.componentStack : ''}
          error={error}
        />
      )
    }

    return children || null
  }
}
